<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import AppDrawer from '@/components/AppDrawer.vue';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';

interface Proposal {
  itemId: number;
  partNumber: string;
  partName: string;
  slot: 'model' | 'drawing';
  documentId: number;
  documentTitle: string;
  reason: string;
  confidence: 'high' | 'medium' | 'low';
  alternatives: number;
}

interface ProposalPayload {
  proposals: Proposal[];
  summary: {
    items: number; documents: number; emptySlots: number; alreadyLinked: number;
    proposed: number; high: number; unknownTypeHits: number;
  };
}

const props = defineProps<{ projectId: string | number }>();
const emit = defineEmits<{ (event: 'close'): void; (event: 'applied', payload: unknown): void }>();

const loading = ref(true);
const applying = ref(false);
const error = ref('');
const payload = ref<ProposalPayload | null>(null);
const picked = ref(new Set<string>());

const CONFIDENCE_LABEL: Readonly<Record<string, string>> = {
  high: '零件号对上', medium: '零件号对上（忽略分隔符）', low: '按零件名推测',
};

const keyOf = (item: Proposal): string => `${item.itemId}-${item.slot}`;
const selectedCount = computed(() => picked.value.size);

async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    payload.value = await eqsApi.previewBomAutoLink(props.projectId) as unknown as ProposalPayload;
    // 默认只勾把握大的；按零件名推测的交给人自己判断
    picked.value = new Set(
      payload.value.proposals.filter((item) => item.confidence !== 'low').map(keyOf),
    );
  } catch (caught) {
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

function toggle(item: Proposal): void {
  const key = keyOf(item);
  const next = new Set(picked.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  picked.value = next;
}

function selectAll(value: boolean): void {
  picked.value = value
    ? new Set((payload.value?.proposals ?? []).map(keyOf))
    : new Set<string>();
}

async function apply(): Promise<void> {
  const links = (payload.value?.proposals ?? [])
    .filter((item) => picked.value.has(keyOf(item)))
    .map((item) => ({ itemId: item.itemId, slot: item.slot, documentId: item.documentId }));
  if (!links.length) return;
  applying.value = true;
  try {
    const result = await eqsApi.applyBomAutoLink(props.projectId, links) as unknown as
      Record<string, unknown> & { applied: number; skipped: number };
    notify(
      'success',
      `已挂上 ${result.applied} 个`,
      result.skipped ? `另有 ${result.skipped} 个位置已被别人挂过，跳过了。` : '可以回表格里核对。',
    );
    emit('applied', result);
    emit('close');
  } catch (caught) {
    notify('error', '挂接失败，未写入', errorMessage(caught));
  } finally {
    applying.value = false;
  }
}

onMounted(load);
</script>

<template>
  <AppDrawer title="按文件名挂接" @close="emit('close')">
    <template #subtitle>
      <p>把项目文件里名字对得上的图纸和模型挂到 BOM 行上。勾选后才会写入。</p>
    </template>

      <p v-if="loading" class="drawer-note">正在比对文件名…</p>
      <p v-else-if="error" class="drawer-note is-error">{{ error }}</p>

      <template v-else-if="payload">
        <p class="drawer-summary">
          {{ payload.summary.items }} 个零件、{{ payload.summary.documents }} 个项目文件，
          找到 {{ payload.summary.proposed }} 个可挂接（{{ payload.summary.high }} 个零件号直接对上）。
          已挂好的 {{ payload.summary.alreadyLinked }} 个位置不动。
        </p>
        <p v-if="payload.summary.unknownTypeHits" class="drawer-note">
          另有 {{ payload.summary.unknownTypeHits }} 个零件匹配到了压缩包一类的文件，看不出是模型还是图纸，需要手工挂。
        </p>

        <div v-if="payload.proposals.length" class="pick-actions">
          <button type="button" @click="selectAll(true)">全选</button>
          <button type="button" @click="selectAll(false)">全不选</button>
          <span>已选 {{ selectedCount }} / {{ payload.proposals.length }}</span>
        </div>

        <ul v-if="payload.proposals.length" class="proposal-list">
          <li v-for="item in payload.proposals" :key="keyOf(item)" :class="`is-${item.confidence}`">
            <label>
              <input type="checkbox" :checked="picked.has(keyOf(item))" @change="toggle(item)">
              <div>
                <b>{{ item.partNumber }} {{ item.partName }}</b>
                <span class="slot">{{ item.slot === 'model' ? '3D 模型' : '2D 图纸' }}</span>
                <span class="doc">{{ item.documentTitle }}</span>
                <small>
                  {{ CONFIDENCE_LABEL[item.confidence] }}
                  <template v-if="item.alternatives">· 另有 {{ item.alternatives }} 个同样对得上的文件</template>
                </small>
              </div>
            </label>
          </li>
        </ul>
        <p v-else class="drawer-note">没有文件名对得上的零件。图纸和模型请先在「文件」页上传。</p>
      </template>

    <template #footer>
      <button type="button" class="inline-button" @click="emit('close')">取消</button>
      <button
        type="button" class="button button-primary"
        :disabled="applying || !selectedCount" @click="apply"
      >{{ applying ? '写入中…' : `挂上选中的 ${selectedCount} 个` }}</button>
    </template>
  </AppDrawer>
</template>

<style scoped>
.drawer-summary { margin: 14px 22px 0; padding: 10px 12px; border-left: 3px solid var(--blue); background: var(--paper); font-size: 12px; line-height: 1.8; }
.drawer-note { margin: 10px 22px 0; color: var(--muted); font-size: 12px; line-height: 1.8; }
.drawer-note.is-error { color: var(--risk); }

.pick-actions { display: flex; align-items: center; gap: 8px; padding: 12px 22px 0; color: var(--muted); font-size: 12px; }
.pick-actions button { padding: 3px 10px; border: 1px solid var(--line); background: var(--white); font-size: 12px; cursor: pointer; }

.proposal-list { flex: 1; overflow-y: auto; margin: 10px 0 0; padding: 0 22px 16px; list-style: none; }
.proposal-list li { border-bottom: 1px solid var(--line); }
.proposal-list li.is-low { background: #fdfaf3; }
.proposal-list label { display: flex; align-items: flex-start; gap: 10px; padding: 10px 0; cursor: pointer; }
.proposal-list label > div { display: grid; gap: 2px; }
.proposal-list b { font-size: 13px; }
.slot { color: var(--blue); font-size: 12px; }
.doc { font-size: 12px; word-break: break-all; }
.proposal-list small { color: var(--muted); font-size: 12px; }

</style>
